import { memo } from 'react'
import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import type { BreakdownRow } from '../../../../shared/types/analytics'

interface BreakdownListProps {
  title: string
  rows: BreakdownRow[]
  emptyMessage?: string
  maxRows?: number
  /** 제목 옆 그림. 여러 카드가 늘어설 때 눈으로 찾기 쉬워진다 */
  icon?: ComponentType<{ className?: string }>
  /** 제목 오른쪽 보조 설명 (예: UTM 의 campaign · source · medium) */
  note?: string
}

const DEFAULT_MAX_ROWS = 8

/**
 * 차원별 상위 항목.
 *
 * 값과 막대를 위아래로 나눈다. 막대를 행 배경으로 깔면 글자가 막대 위에
 * 겹쳐 대비가 들쭉날쭉해진다. 1위만 진한 색을 줘 어느 것이 으뜸인지
 * 한눈에 보이게 한다.
 */
function BreakdownList({
  title,
  rows,
  emptyMessage,
  maxRows = DEFAULT_MAX_ROWS,
  icon: Icon,
  note,
}: BreakdownListProps) {
  const { t } = useTranslation()
  const visible = rows.slice(0, maxRows)

  return (
    <section className="flex flex-col gap-3.5 rounded-2xl border border-gray-200 bg-white px-5 pb-5 pt-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2">
        {Icon && (
          <Icon className="h-[17px] w-[17px] shrink-0 text-primary-600 dark:text-primary-400" />
        )}
        <h3 className="flex-1 truncate text-[15px] font-bold text-gray-900 dark:text-white">
          {title}
        </h3>
        {note && (
          <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
            {note}
          </span>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
          {emptyMessage ?? t('analyticsPage.noClicks')}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {visible.map((row, index) => (
            <li key={row.label} className="flex flex-col gap-1.5">
              <div className="flex items-baseline gap-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-gray-200">
                  {row.label}
                </span>
                <span className="font-bold tabular-nums text-gray-900 dark:text-white">
                  {row.clicks.toLocaleString()}
                </span>
                <span className="w-12 text-right text-xs tabular-nums text-gray-400 dark:text-gray-500">
                  {row.percentage}%
                </span>
              </div>
              <span className="block h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <span
                  className={`block h-full rounded-full ${
                    index === 0
                      ? 'bg-primary-600'
                      : 'bg-primary-300 dark:bg-primary-700'
                  }`}
                  style={{ width: `${Math.max(row.percentage, 2)}%` }}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export default memo(BreakdownList)
